import React, { useState, useEffect, useRef } from 'react';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import {
  Formik,
  Form,
  Field,
  yupToFormErrors,
  setNestedObjectValues,
  FieldArray
} from 'formik';
import Input from '@hobs/components/Input';
import Money from '@hobs/components/Money';
import cx from 'classnames';
import { GhostButton } from '@hobs/components/Buttons';
import Icon from '@hobs/components/Icon';
import Dropdown from '@hobs/components/Dropdown';
import Dialog from '@hobs/components/Dialog';
import DropZone from '@hobs/components/DropZone';
import Confirm from '@hobs/components/Confirm';
import * as Yup from 'yup';
import { withRouter } from 'react-router-dom';
import ShortId from 'shortid';
import Quantity from '../Quantity';
import Status from '../Status';
import { DELIVERY_LINE_STATUSES } from '@hobs/utils/constants';

const AssignmentDelivery = ({
  deliveryLines = [],
  t,
  readOnly,
  onDeliverySubmit,
  vatOptions,
  getSelectedVat,
  disabled,
  onFileSelect,
  isDeliveryFileDialogOpen,
  onDeliveryFileDialogOpen,
  onDeliveryFileDialogClose,
  onDeliveryRollback,
  exerciseCurrency,
  showCurrency,
  totals,
  responseQuantity,
  canConfirmDeliveryLines
}) => {
  const [key, setKey] = useState(ShortId.generate());
  const [
    deliveryQuantityApprovalRequired,
    setDeliveryQuantityApprovalRequired
  ] = useState(false);

  // https://reactjs.org/docs/hooks-faq.html#how-to-get-the-previous-props-or-state
  function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }

  const prevDelivery = usePrevious(deliveryLines);

  useEffect(() => {
    if (JSON.stringify(deliveryLines) !== JSON.stringify(prevDelivery)) {
      onRollBackUpdate();
    }
  });

  const onRollBackUpdate = () => {
    setKey(ShortId.generate());
  };

  const onFormSubmit = (values, actions) => {
    Yup.object()
      .shape({
        deliveryLines: Yup.array().of(
          Yup.object().shape({
            quantity: Yup.number()
              .nullable()
              .required(t('Quantity is required'))
              .typeError('Quantity is required')
              .positive(t('Quantity must be greater than 0')),
            priceUnit: Yup.number()
              .required(t('Price is required'))
              .typeError('Price is required')
              .min(0, t('Price must be equal to or greater than 0'))
              .nullable(),
            priceTotal: Yup.number()
              .required(t('Total cost is required'))
              .typeError('Total cost is required')
              .min(0, t('Total cost must be equal to or greater than 0'))
              .nullable(),
            valueAddedTaxId: Yup.string()
              .required(t('VAT is required'))
              .nullable(),
            comment: Yup.string()
              .max(1500, t('Comment cannot be longer than 500 symbols'))
              .nullable()
          })
        )
      })
      .validate(values, { abortEarly: false })
      .catch(errors => {
        actions.setErrors(yupToFormErrors(errors));
        actions.setTouched(setNestedObjectValues(values, true));
        actions.setSubmitting(false);
      })
      .then(response => {
        if (response) {
          if (!isQuantityApprovalRequired(values)) {
            onDeliverySubmit(
              values.deliveryLines[values.selectedDeliveryLineIndex],
              actions
            );
          } else {
            setDeliveryQuantityApprovalRequired(true);
          }
        }
      });
  };

  function isQuantityApprovalRequired(values) {
    if (!values || !values.deliveryLines) {
      return false;
    }

    const totalDeliveryLineQuantity = values.deliveryLines.reduce(
      (acc, line) => acc + line.quantity,
      0
    );

    return responseQuantity < totalDeliveryLineQuantity;
  }

  function getDefaultVat() {
    return getSelectedVat ? getSelectedVat().valueAddedTaxId : null;
  }

  function calcDeliveryUnitCost(quantity, totalCost, setFieldValue, index) {
    setFieldValue(`deliveryLines[${index}].priceTotal`, totalCost);

    if (!quantity) {
      return;
    }
    if (!totalCost && totalCost !== 0) {
      totalCost = 0;
    }

    setFieldValue(
      `deliveryLines[${index}].priceUnit`,
      (totalCost / quantity).toFixed(2)
    );
  }

  function calcDeliveryTotalCost(quantity, unitCost, setFieldValue, index) {
    setFieldValue(`deliveryLines[${index}].priceUnit`, unitCost);

    if (!unitCost && unitCost !== 0) {
      unitCost = 0;
    }
    if (!quantity && quantity !== 0) {
      quantity = 0;
    }

    setFieldValue(
      `deliveryLines[${index}].priceTotal`,
      (unitCost * quantity).toFixed(2)
    );
  }

  function getInitialValues() {
    return {
      selectedDeliveryLineIndex: 0,
      deliveryLines: deliveryLines
    };
  }

  return (
    <div className="assignment-response">
      <Formik
        validateOnBlur={false}
        validateOnChange={false}
        enableReinitialize={true}
        initialValues={getInitialValues()}
        onSubmit={onFormSubmit}
        key={key}
      >
        {({
          handleSubmit,
          setFieldValue,
          values,
          isSubmitting,
          setSubmitting
        }) => {
          return (
            <>
              {values &&
                values.deliveryLines &&
                values.deliveryLines.length > 0 && (
                  <Dialog
                    title={t('Delivery files')}
                    isOpen={isDeliveryFileDialogOpen}
                    onClose={onDeliveryFileDialogClose}
                    className="response-files"
                  >
                    <DropZone
                      onSelect={files => {
                        onFileSelect(files).then(response => {
                          setFieldValue(
                            `deliveryLines[${values.selectedDeliveryLineIndex}].attachments`,
                            values.deliveryLines[
                              values.selectedDeliveryLineIndex
                            ].attachments.concat(response.payload)
                          );
                        });
                      }}
                      onRemove={(file, list) => {
                        setFieldValue(
                          `deliveryLines[${values.selectedDeliveryLineIndex}].attachments`,
                          list
                        );
                      }}
                      value={
                        values.deliveryLines[values.selectedDeliveryLineIndex]
                          .attachments
                          ? values.deliveryLines[
                              values.selectedDeliveryLineIndex
                            ].attachments
                          : []
                      }
                      key={ShortId.generate()}
                      disabled={
                        !values.deliveryLines[values.selectedDeliveryLineIndex]
                          .isEditable || readOnly
                      }
                      showSuccessMessages={true}
                    />
                  </Dialog>
                )}
              <Form noValidate={true}>
                <table className="table">
                  <thead>
                    {values &&
                      values.deliveryLines &&
                      values.deliveryLines.length > 0 && (
                        <tr>
                          <th>{t('Line')}</th>
                          <th>{t('Status')}</th>
                          <th>{t('Delivery')}</th>
                          {showCurrency && <th>{t('Currency')}</th>}
                          <th>{t('Price')}</th>
                          <th>{t('Total')}</th>
                          <th>{t('VAT')}</th>
                          <th>{t('Gross amount')}</th>
                          <th>{t('Comment')}</th>
                          <th>{t('Files')}</th>
                          {!disabled && <th />}
                          {!disabled && <th />}
                        </tr>
                      )}
                  </thead>
                  <tbody>
                    {!disabled && (
                      <FieldArray
                        name="deliveryLines"
                        render={arrayHelpers => (
                          <React.Fragment>
                            {values.deliveryLines.map((delivery, index) => {
                              return (
                                <React.Fragment key={index}>
                                  <tr>
                                    <td className="xsmall">
                                      {delivery.positionInLine}
                                    </td>
                                    <td className="request-information--edit-cell--status">
                                      {delivery.vatCode && (
                                        <Status
                                          name={
                                            DELIVERY_LINE_STATUSES[
                                              delivery.status
                                            ].type
                                          }
                                          type={delivery.status}
                                          statuses={DELIVERY_LINE_STATUSES}
                                        />
                                      )}
                                    </td>
                                    <td className="small">
                                      {readOnly && (
                                        <Quantity amount={delivery.quantity} />
                                      )}
                                      {!readOnly && (
                                        <Field
                                          noMargin
                                          disabled={
                                            !delivery.isEditable || readOnly
                                          }
                                          component={Input}
                                          type="number"
                                          name={`deliveryLines[${index}].quantity`}
                                          onChange={event => {
                                            calcDeliveryTotalCost(
                                              event.target.value,
                                              delivery.priceUnit,
                                              setFieldValue,
                                              index
                                            );
                                          }}
                                        />
                                      )}
                                    </td>
                                    {showCurrency && (
                                      <td className="small">
                                        {readOnly && (
                                          <span>{exerciseCurrency}</span>
                                        )}
                                        {!readOnly && (
                                          <Input
                                            name="exerciseCurrency"
                                            disabled={true}
                                            noMargin
                                            type="text"
                                            value={exerciseCurrency}
                                          />
                                        )}
                                      </td>
                                    )}
                                    <td className="small">
                                      {readOnly && (
                                        <Money amount={delivery.quantity} />
                                      )}
                                      {!readOnly && (
                                        <Field
                                          name={`deliveryLines[${index}].priceUnit`}
                                          component={Input}
                                          noMargin
                                          min={0}
                                          disabled={!delivery.isEditable}
                                          setFieldValue={setFieldValue}
                                          type="number"
                                          onChange={event => {
                                            calcDeliveryTotalCost(
                                              delivery.quantity,
                                              event.target.value,
                                              setFieldValue,
                                              index
                                            );
                                          }}
                                        />
                                      )}
                                      <Field
                                        name={`deliveryLines[${index}].priceUnit`}
                                        min={0}
                                        type="hidden"
                                      />
                                    </td>
                                    <td className="small">
                                      {readOnly && (
                                        <Money amount={delivery.priceTotal} />
                                      )}
                                      {!readOnly && (
                                        <Field
                                          name={`deliveryLines[${index}].priceTotal`}
                                          component={Input}
                                          noMargin
                                          min={0}
                                          type="number"
                                          disabled={!delivery.isEditable}
                                          setFieldValue={setFieldValue}
                                          onChange={event => {
                                            calcDeliveryUnitCost(
                                              delivery.quantity,
                                              event.target.value,
                                              setFieldValue,
                                              index
                                            );
                                          }}
                                        />
                                      )}
                                      <Field
                                        name={`deliveryLines[${index}].priceTotal`}
                                        min={0}
                                        type="hidden"
                                      />
                                    </td>
                                    <td className="small">
                                      {readOnly && (
                                        <Money amount={delivery.priceTotal} />
                                      )}
                                      {!readOnly && (
                                        <Field
                                          noMargin
                                          setFieldValue={setFieldValue}
                                          component={Dropdown}
                                          name={`deliveryLines[${index}].valueAddedTaxId`}
                                          disabled={!delivery.isEditable}
                                        >
                                          <option value="">
                                            {t('Select VAT')}
                                          </option>
                                          {vatOptions}
                                        </Field>
                                      )}
                                    </td>
                                    {!disabled && (
                                      <td>
                                        <Money
                                          amount={
                                            delivery.priceTotal *
                                            (getSelectedVat(
                                              delivery.valueAddedTaxId
                                            ).percentage /
                                              100 +
                                              1)
                                          }
                                        />
                                      </td>
                                    )}
                                    <td>
                                      {readOnly && (
                                        <Money amount={delivery.priceTotal} />
                                      )}
                                      {!readOnly && (
                                        <Field
                                          noMargin
                                          component={Input}
                                          name={`deliveryLines[${index}].comment`}
                                          disabled={!delivery.isEditable}
                                        />
                                      )}
                                    </td>
                                    <td className="small">
                                      <div
                                        onClick={() => {
                                          setFieldValue(
                                            'selectedDeliveryLineIndex',
                                            index
                                          );
                                          onDeliveryFileDialogOpen();
                                        }}
                                        className="response-attachment-link"
                                      >
                                        <Icon
                                          icon="attachment"
                                          className={cx('files-icon', {
                                            'no-files': !delivery.attachments
                                              .length
                                          })}
                                        />
                                        {delivery.attachments.length
                                          ? `(${delivery.attachments.length})`
                                          : ''}
                                      </div>
                                    </td>
                                    <td></td>
                                    <td className="controls">
                                      <GhostButton
                                        disabled={
                                          isSubmitting ||
                                          readOnly ||
                                          !delivery.isEditable
                                        }
                                        onClick={async () => {
                                          setFieldValue(
                                            'selectedDeliveryLineIndex',
                                            index
                                          );
                                          handleSubmit();
                                        }}
                                        type="button"
                                      >
                                        {t('Confirm delivery')}
                                      </GhostButton>
                                      {!delivery.vatCode && (
                                        <GhostButton
                                          onClick={async () => {
                                            arrayHelpers.pop();
                                          }}
                                          type="button"
                                        >
                                          {t('Cancel')}
                                        </GhostButton>
                                      )}
                                      {deliveryQuantityApprovalRequired &&
                                        Confirm(
                                          t('Delivery Quantity'),
                                          t(
                                            'Delivery quantity is greater than was responded. Are you sure, you want to confirm?'
                                          ),
                                          async () => {
                                            setDeliveryQuantityApprovalRequired(
                                              false
                                            );
                                            onDeliverySubmit(
                                              values.deliveryLines[
                                                values.selectedDeliveryLineIndex
                                              ],
                                              {
                                                setSubmitting
                                              }
                                            );
                                          },
                                          () => {
                                            setDeliveryQuantityApprovalRequired(
                                              false
                                            );
                                            setSubmitting(false);
                                          }
                                        )}
                                      {delivery.canRollBack && (
                                        <GhostButton
                                          disabled={
                                            isSubmitting ||
                                            disabled ||
                                            !delivery.isEditable
                                          }
                                          onClick={async () => {
                                            Confirm(
                                              t('Rollback'),
                                              t(
                                                'Are you sure you want to rollback this line?'
                                              ),
                                              async () => {
                                                await onDeliveryRollback(
                                                  delivery.deliveryLineId
                                                );
                                              }
                                            );
                                          }}
                                          type="button"
                                        >
                                          {t('Rollback')}
                                        </GhostButton>
                                      )}
                                    </td>
                                  </tr>
                                </React.Fragment>
                              );
                            })}
                            {values &&
                              values.deliveryLines &&
                              values.deliveryLines.length > 0 && (
                                <tr>
                                  <td colSpan="2">
                                    <strong>{t('Total')}</strong>
                                  </td>
                                  <td colSpan={showCurrency ? 3 : 2}>
                                    <strong>
                                      <Quantity amount={totals.totalQuantity} />
                                    </strong>
                                  </td>
                                  <td colSpan="2">
                                    <strong>
                                      <Money
                                        amount={totals.totalPrice}
                                        currency={exerciseCurrency}
                                      />
                                    </strong>
                                  </td>
                                  <td colSpan="4">
                                    <strong>
                                      <Money
                                        amount={totals.totalGrossAmount}
                                        currency={exerciseCurrency}
                                      />
                                    </strong>
                                  </td>
                                </tr>
                              )}
                            <div className="request-information--assignment-action">
                              <GhostButton
                                iconLeft={<Icon icon="Plus" />}
                                type="button"
                                onClick={() => {
                                  arrayHelpers.push({
                                    status: 1,
                                    attachments: [],
                                    isEditable: true,
                                    valueAddedTaxId: getDefaultVat(),
                                    priceUnit: '',
                                    priceTotal: '',
                                    quantity: ''
                                  });
                                }}
                                disabled={disabled || !canConfirmDeliveryLines}
                              >
                                {t('Add new delivery')}
                              </GhostButton>
                            </div>
                          </React.Fragment>
                        )}
                      ></FieldArray>
                    )}
                    {disabled &&
                      deliveryLines &&
                      deliveryLines.map(delivery => {
                        return (
                          <tr>
                            <td>{delivery.positionInLine}</td>
                            <td>
                              <Status
                                name={
                                  DELIVERY_LINE_STATUSES[delivery.status].type
                                }
                                type={delivery.status}
                                statuses={DELIVERY_LINE_STATUSES}
                              />
                            </td>
                            <td>
                              <Quantity amount={delivery.quantity} />
                            </td>
                            <td>
                              <span>{exerciseCurrency}</span>
                            </td>
                            <td>
                              <Money amount={delivery.priceUnit} />
                            </td>
                            <td>
                              <Money amount={delivery.priceTotal} />
                            </td>
                            <td>
                              <span>{delivery.vatPercentage}%</span>
                            </td>
                            <td>
                              <Money amount={delivery.priceGrossAmount} />
                            </td>
                            <td>{delivery.comment}</td>
                            <td className="small">
                              <div
                                onClick={onDeliveryFileDialogOpen}
                                className="response-attachment-link"
                              >
                                <Icon
                                  icon="attachment"
                                  className={cx('files-icon', {
                                    'no-files': !delivery.attachments.length
                                  })}
                                />
                                {delivery.attachments.length
                                  ? `(${delivery.attachments.length})`
                                  : ''}
                              </div>
                            </td>
                            <td></td>
                            <td></td>
                          </tr>
                        );
                      })}
                    {disabled && deliveryLines && (
                      <tr>
                        <td colSpan="2">
                          <strong>{t('Total')}</strong>
                        </td>
                        <td colSpan={showCurrency ? 3 : 2}>
                          <strong>
                            <Quantity amount={totals.totalQuantity} />
                          </strong>
                        </td>
                        <td colSpan="2">
                          <strong>
                            <Money
                              amount={totals.totalPrice}
                              currency={exerciseCurrency}
                            />
                          </strong>
                        </td>
                        <td colSpan="5">
                          <strong>
                            <Money
                              amount={totals.totalGrossAmount}
                              currency={exerciseCurrency}
                            />
                          </strong>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </Form>
            </>
          );
        }}
      </Formik>
    </div>
  );
};

AssignmentDelivery.propTypes = {
  deliveryLines: PropTypes.array,
  t: PropTypes.func,
  onSubmit: PropTypes.func,
  onDeliverySubmit: PropTypes.func,
  vatOptions: PropTypes.array,
  getSelectedVat: PropTypes.func,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  onFileSelect: PropTypes.func,
  isDeliveryFileDialogOpen: PropTypes.bool,
  onDeliveryFileDialogOpen: PropTypes.func,
  onDeliveryFileDialogClose: PropTypes.func,
  onSharedCostChange: PropTypes.func,
  onEarlyConfirmationRequiredChange: PropTypes.func,
  onDeliveryFinalizedChange: PropTypes.func,
  onFileDialogOpen: PropTypes.func,
  onDeliveryRollback: PropTypes.func,
  isEarlyConfirmationVisible: PropTypes.bool,
  totals: PropTypes.object,
  responseQuantity: PropTypes.number,
  canConfirmDeliveryLines: PropTypes.bool
};

export default withRouter(withTranslation()(AssignmentDelivery));
